import {
  ArticleAlert,
  ArticleAside,
  ArticleList,
  ArticleListItem,
  ArticleParagraph,
  ArticleRelatedLink,
  ArticleSection,
  ArticleSectionHeader,
  ArticleSectionSubHeader,
} from "@/components/primitives/article";
import { UnderlineLink } from "@/components/primitives/underline-link";
import { FeedbackLink } from "@/components/feedback-dialog";
import { slugify } from "@/lib/utils";
import {
  Children,
  isValidElement,
  type ComponentProps,
  type ReactNode,
} from "react";
import { z } from "zod";

const textNodeSchema = z.union([z.string(), z.number()]);

function getNodeText(node: ReactNode): string {
  const textNode = textNodeSchema.safeParse(node);
  if (textNode.success) {
    return String(textNode.data);
  }

  if (Array.isArray(node)) {
    return node.map(getNodeText).join("");
  }

  if (isValidElement<{ children?: ReactNode }>(node)) {
    return getNodeText(node.props.children);
  }

  return "";
}

function MdxH2({
  children,
  id,
  ...props
}: ComponentProps<typeof ArticleSectionHeader>) {
  const text = Children.toArray(children).map(getNodeText).join("").trim();
  const headingId = id ?? slugify(text);

  return (
    <ArticleSectionHeader {...props} id={headingId}>
      {children}
    </ArticleSectionHeader>
  );
}

function MdxLink({ href, ...props }: ComponentProps<"a">) {
  const isNativeLink = !href || /^(?:[a-z][a-z\d+.-]*:|\/\/|#|\?)/i.test(href);

  if (isNativeLink) {
    return (
      <UnderlineLink
        external
        href={href}
        target={undefined}
        rel={undefined}
        {...props}
      />
    );
  }

  return <UnderlineLink to={href} {...props} />;
}

export const mdxComponents = {
  section: ArticleSection,
  h2: MdxH2,
  h3: ArticleSectionSubHeader,
  p: ArticleParagraph,
  ul: ArticleList,
  li: ArticleListItem,
  a: MdxLink,
  ArticleAlert,
  ArticleAside,
  ArticleRelatedLink,
  FeedbackLink,
};
